import { useCallback, useEffect, useId, useState } from "react";
import type { Locale } from "../../shared/dto";
import { WorkingAvatar } from "../WorkingAvatar";
import { formatDate } from "./core";
import { isOfflineMutation, staffMutationErrorKey, type StaffDetail, type StaffRun } from "./model";
import {
  PARTNER_BRIEF_MAX_CHARS,
  PARTNER_BRIEF_SCOPES,
  briefScopeForStep,
  type MaterialRegenerationEntry,
  type PartnerBriefEntry,
  type PartnerBriefMaterialScope,
  type PartnerBriefScope
} from "../../shared/partner-briefs";
import "./partner-brief-panel.css";

/**
 * F106 (Pavel 2026-08-10): the partner's own briefing for material generation.
 *
 * "Ten parťák je parťák toho člověka. A když bude moct ten prompt nějakým
 * způsobem autoritativně ovlivnit, tak to hodně pomůže."
 *
 * Two placements, one component:
 *   - the client card (`step` absent) shows ALL five fields, so the partner can
 *     brief every material BEFORE anything is generated ("tím pádem to pak
 *     nebudu muset přegenerovávat");
 *   - a material step shows the global note, that material's note, and the
 *     rebuild-from-scratch action, which belongs to the material it rebuilds.
 *
 * 🔴 Staff surface only. The notes may quote a call the client had with their
 * partner; the client never sees this panel or its data.
 */

interface MaterialFactsDto {
  scope: PartnerBriefMaterialScope;
  latestVersion: number | null;
  status: string | null;
  published: boolean;
  openNotes: number;
  buildRunning: boolean;
  editRunning: boolean;
  regeneration: MaterialRegenerationEntry | null;
  regenerating: boolean;
}

interface BriefsPayload {
  briefs: PartnerBriefEntry[];
  materials: MaterialFactsDto[];
}

type Saved = "idle" | "saving" | "saved" | "error";

export function PartnerBriefPanel(props: {
  detail: StaffDetail;
  locale: Locale;
  t: (key: string) => string;
  run: StaffRun;
  /** Rail step; absent on the client card, where every field is shown. */
  step?: string;
}) {
  const { detail, t } = props;
  const clientId = detail.client.id;
  const materialScope = props.step ? briefScopeForStep(props.step) : null;
  // A non-material step has no briefing of its own; rendering the global note
  // on the know-how step would put the same field in ten places.
  const active = !props.step || Boolean(materialScope);
  const scopes: PartnerBriefScope[] = materialScope ? ["global", materialScope] : [...PARTNER_BRIEF_SCOPES];

  const panelId = useId();
  const [payload, setPayload] = useState<BriefsPayload | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, Saved>>({});
  /** Which scopes the partner has opened. Undefined = the row decides. */
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // The failure's own sentence per field: a lost connection is not the same as
  // a server that refused, and the partner may have several fields open.
  const [saveError, setSaveError] = useState<Record<string, string>>({});
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/api/staff/clients/${encodeURIComponent(clientId)}/partner-briefs`, {
        credentials: "include"
      });
      if (!response.ok) { setLoadFailed(true); return; }
      const body = await response.json() as BriefsPayload & { ok?: boolean };
      setLoadFailed(false);
      setPayload({ briefs: body.briefs ?? [], materials: body.materials ?? [] });
      // 🔴 Server text seeds a field ONLY when the partner has no unsaved
      // draft in it. A background refresh that overwrote what someone is
      // typing would eat their work mid-sentence.
      setDrafts((current) => {
        const next = { ...current };
        for (const brief of body.briefs ?? []) {
          if (next[brief.scope] === undefined) next[brief.scope] = brief.text;
        }
        return next;
      });
    } catch {
      setLoadFailed(true);
    }
  }, [clientId]);

  useEffect(() => {
    if (!active) return;
    setPayload(null);
    setDrafts({});
    setSaved({});
    setSaveError({});
    setOpen({});
    void load();
  }, [active, clientId, load]);

  if (!active) return null;

  const briefOf = (scope: PartnerBriefScope) => payload?.briefs.find((row) => row.scope === scope) ?? null;
  const draftOf = (scope: PartnerBriefScope) => drafts[scope] ?? briefOf(scope)?.text ?? "";
  const dirty = (scope: PartnerBriefScope) => draftOf(scope) !== (briefOf(scope)?.text ?? "");

  async function save(scope: PartnerBriefScope) {
    const text = draftOf(scope);
    setSaved((current) => ({ ...current, [scope]: "saving" }));
    const response = await props.run(
      `/api/staff/clients/${encodeURIComponent(clientId)}/partner-briefs/${scope}`,
      { method: "PUT", body: { text } },
      // No console-wide refresh: the partner may be typing in the next field.
      { refresh: false }
    );
    if (!response.ok) {
      setSaveError((current) => ({
        ...current,
        [scope]: staffMutationErrorKey(response, "partnerBriefs.saveFailed")
      }));
      setSaved((current) => ({ ...current, [scope]: "error" }));
      return;
    }
    const body = await response.json().catch(() => null) as { brief?: PartnerBriefEntry | null } | null;
    setPayload((current) => {
      if (!current) return current;
      const others = current.briefs.filter((row) => row.scope !== scope);
      return { ...current, briefs: body?.brief ? [...others, body.brief] : others };
    });
    setSaved((current) => ({ ...current, [scope]: "saved" }));
  }

  return (
    <article className="staff-card partner-brief-panel" data-partner-briefs={materialScope ?? "all"}>
      <h3>{t("partnerBriefs.title")}</h3>
      <p className="staff-card-hint">{t("partnerBriefs.hint")}</p>
      {loadFailed && <p className="error-message">{t("partnerBriefs.loadFailed")}</p>}
      {/* 🔴 F138 kolečko 3, fault 5: five rows, not five walls.
          On the client card this panel showed all five scopes as full,
          always-open textareas - measured 1 382 px of identical empty boxes,
          each with its own Uložit, on a client nobody had briefed. An empty
          field is not information; the NAME of the field plus what stands in
          it is. So a scope is a row carrying its own first line, and the box
          opens on the row. Nothing about the content or the API changed: the
          same five scopes, the same PUT, the same draft protection. */}
      <div className="partner-brief-fields">
        {scopes.map((scope) => {
          const brief = briefOf(scope);
          const state = saved[scope] ?? "idle";
          const text = draftOf(scope);
          // A field the partner is in stays in: an unsaved draft or a live
          // result must never be folded away underneath them.
          const isOpen = open[scope] ?? (dirty(scope) || state === "saving" || state === "error");
          const preview = text.split("\n").map((line) => line.trim()).find(Boolean) ?? "";
          const fieldId = `${panelId}-${scope}`;
          return (
            <div key={scope} className="partner-brief-field" data-scope={scope} data-open={isOpen ? "true" : "false"}>
              <button
                aria-controls={fieldId}
                aria-expanded={isOpen}
                className="partner-brief-row-toggle"
                type="button"
                onClick={() => setOpen((current) => ({ ...current, [scope]: !isOpen }))}
              >
                <span className="partner-brief-label">{t(`partnerBriefs.scope.${scope}`)}</span>
                <span className="partner-brief-preview" data-empty={preview ? "false" : "true"}>
                  {/* 🔴 A hyphen, never an em or en rule: those two code points
                      are banned repo-wide and `check:locales` fails on them.
                      The mark is what a reader scans past; the sentence behind
                      it is what a screen reader hears. */}
                  {preview || <span aria-label={t("partnerBriefs.emptyNote")}>-</span>}
                </span>
                {/* 🔴 The row shows the DRAFT, so a collapsed row would show
                    unsaved text exactly as it shows saved text - the surface
                    telling the partner their note is filed when it is not.
                    A row that is folded over an unsaved change says so. */}
                {!isOpen && dirty(scope) && (
                  <span className="partner-brief-dirty">{t("partnerBriefs.unsaved")}</span>
                )}
              </button>
              {isOpen && (
                <div className="partner-brief-body" id={fieldId}>
                  <label className="partner-brief-sublabel" htmlFor={`${fieldId}-input`}>
                    {t(`partnerBriefs.scopeHint.${scope}`)}
                  </label>
                  <textarea
                    id={`${fieldId}-input`}
                    rows={scope === "global" ? 5 : 4}
                    maxLength={PARTNER_BRIEF_MAX_CHARS}
                    value={text}
                    placeholder={t(`partnerBriefs.placeholder.${scope}`)}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDrafts((current) => ({ ...current, [scope]: value }));
                      setSaved((current) => ({ ...current, [scope]: "idle" }));
                    }}
                  />
                  <span className="partner-brief-row">
                    <button
                      className="secondary-button compact-button"
                      type="button"
                      disabled={!dirty(scope) || state === "saving"}
                      onClick={() => void save(scope)}
                    >
                      {t("partnerBriefs.save")}
                    </button>
                    {state === "saved" && !dirty(scope) && (
                      <small className="partner-brief-saved">{t("partnerBriefs.savedNote")}</small>
                    )}
                    {state === "error" && (
                      <small className="error-message">{t(saveError[scope] ?? "partnerBriefs.saveFailed")}</small>
                    )}
                    {state !== "error" && dirty(scope) && (
                      <small className="partner-brief-dirty">{t("partnerBriefs.unsaved")}</small>
                    )}
                    {brief && !dirty(scope) && state !== "saved" && (
                      <small className="partner-brief-meta">
                        {t("partnerBriefs.lastSaved")
                          .replace("{name}", brief.updatedByName ?? t("staffConsole.none"))
                          .replace("{date}", formatDate(brief.updatedAt, props.locale))}
                      </small>
                    )}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {materialScope && (
        <RegenerateBlock
          clientId={clientId}
          clientName={detail.client.name}
          scope={materialScope}
          facts={payload?.materials.find((row) => row.scope === materialScope) ?? null}
          locale={props.locale}
          t={t}
          run={props.run}
          onDone={() => void load()}
        />
      )}
    </article>
  );
}

/**
 * The refusal codes this panel has a sentence for. Kept beside the component
 * that prints them, so adding a locale key and adding the code here is one
 * edit; a code missing from BOTH is still answered, by the generic sentence.
 */
/**
 * Grow a textarea to its own content. `height:auto` first, or the box can only
 * ever get taller: scrollHeight of an already-stretched element reports the
 * stretched height. A `resize: vertical` handle still works afterwards, the
 * next keystroke simply re-measures.
 */
function autoGrow(element: HTMLTextAreaElement | null): void {
  if (!element) return;
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
}

const REGENERATE_ERROR_ALIASES: Record<string, string> = {
  // mutationSecurity names three separate refusals; all three mean the same
  // thing to the partner, and the generic sentence would invite a retry that
  // cannot work.
  origin_invalid: "forbidden",
  csrf_invalid: "forbidden",
  impersonation_readonly: "forbidden"
};

const REGENERATE_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  "nothing_to_regenerate",
  "build_running",
  "edit_in_progress",
  "regeneration_in_progress",
  "prerequisite_missing",
  "reason_required",
  "state_conflict",
  "storage_unavailable",
  "forbidden"
]);

/**
 * "Přegenerovat od základu (s podklady)".
 *
 * Pavel drew the line himself: this is for "hrubě nespokojený, chce to fakt
 * jinak", not for detail work, which the comment system already covers. So the
 * control asks for a written reason, spells out what happens to the version the
 * client can see and to the open comments, and refuses in named sentences when
 * a build or an edit is already running.
 */
function RegenerateBlock(props: {
  clientId: string;
  clientName: string;
  scope: PartnerBriefMaterialScope;
  facts: MaterialFactsDto | null;
  locale: Locale;
  t: (key: string) => string;
  run: StaffRun;
  onDone: () => void;
}) {
  const { facts, t } = props;
  const [armed, setArmed] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!facts) return null;

  if (facts.regenerating) {
    return (
      <div className="partner-brief-regenerate is-running">
        <WorkingAvatar size="card" />
        <div>
          <h4>{t("partnerBriefs.regenerate.runningTitle")}</h4>
          <p>{t("partnerBriefs.regenerate.runningBody")}</p>
          {facts.regeneration && <p className="partner-brief-reason">{facts.regeneration.reason}</p>}
        </div>
      </div>
    );
  }

  // Nothing to rebuild yet is not a failure: it is the case the notes above
  // were designed for, so the panel says so instead of showing a dead button.
  if (facts.latestVersion === null) {
    return (
      <div className="partner-brief-regenerate is-empty">
        <p>{t("partnerBriefs.regenerate.notYet")}</p>
      </div>
    );
  }

  const blockedKey = facts.buildRunning
    ? "partnerBriefs.regenerate.blockedBuild"
    : facts.editRunning
      ? "partnerBriefs.regenerate.blockedEdit"
      : null;

  // 🔴 `translate` THROWS on a missing key, so a code that has no sentence must
  // never reach it: the throw would escape `fire()` before `setBusy(false)` and
  // leave the confirm button disabled forever with nothing said (the refusal
  // that can never clear, fix2-chyby). The server can answer with codes this
  // panel does not name - invalid_request, not_found, idempotency_mismatch and
  // the three mutationSecurity codes - so the allowlist decides, exactly like
  // NAMED_CODES in src/action-error.ts, and everything else is `generic`.
  const errorSentence = (raw: string) => {
    const code = REGENERATE_ERROR_ALIASES[raw] ?? raw;
    return t(REGENERATE_ERROR_CODES.has(code)
      ? `partnerBriefs.regenerate.error.${code}`
      : "partnerBriefs.regenerate.error.generic");
  };

  async function fire() {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setError(t("partnerBriefs.regenerate.reasonRequired"));
      return;
    }
    setBusy(true);
    setError(null);
    const response = await props.run(
      `/api/staff/clients/${encodeURIComponent(props.clientId)}/materials/${props.scope}/regenerate`,
      { method: "POST", body: { reason: trimmed } }
    );
    if (response.ok) {
      setArmed(false);
      setReason("");
      props.onDone();
    } else if (isOfflineMutation(response)) {
      // The browser never reached the server, so nothing was requested and the
      // one sentence about a lost connection wins over the panel's own copy.
      setError(t("staffConsole.mutationOffline"));
    } else {
      let code = "generic";
      try {
        const body = await response.json() as { error?: string };
        if (typeof body.error === "string") code = body.error;
      } catch { /* the generic sentence below covers an unreadable body */ }
      setError(errorSentence(code));
    }
    setBusy(false);
  }

  return (
    <div className="partner-brief-regenerate">
      <h4>{t("partnerBriefs.regenerate.title")}</h4>
      <p className="staff-card-hint">{t("partnerBriefs.regenerate.hint")}</p>
      {blockedKey ? (
        <p className="partner-brief-blocked">{t(blockedKey)}</p>
      ) : !armed ? (
        <button className="secondary-button compact-button" type="button" onClick={() => { setArmed(true); setError(null); }}>
          {t("partnerBriefs.regenerate.button")}
        </button>
      ) : (
        <div className="partner-brief-confirm" role="alert">
          <p>
            {t("partnerBriefs.regenerate.confirm")
              .replace("{material}", t(`partnerOS.material.${props.scope}`))
              .replace("{client}", props.clientName)}
          </p>
          <ul className="partner-brief-consequences">
            <li>{t("partnerBriefs.regenerate.consequenceFresh")}</li>
            {facts.published && <li>{t("partnerBriefs.regenerate.consequencePublished")}</li>}
            {facts.status === "approved" && <li>{t("partnerBriefs.regenerate.consequenceApproved")}</li>}
            {facts.openNotes > 0 && (
              <li>{t("partnerBriefs.regenerate.consequenceNotes").replace("{count}", String(facts.openNotes))}</li>
            )}
          </ul>
          <label className="partner-brief-reason-field">
            <span>{t("partnerBriefs.regenerate.reasonLabel")}</span>
            {/* 🔴 The box GROWS with the sentence. At 390px a three-row box
                holds 91px of a 114px reason and slices the last word in half,
                so the partner commits a sentence they cannot read - and this
                one is not decoration: it steers the rebuild and becomes the
                audit record. Measured in artifacts/f106-partner-briefs/probe.mjs. */}
            <textarea
              ref={autoGrow}
              rows={3}
              value={reason}
              placeholder={t("partnerBriefs.regenerate.reasonPlaceholder")}
              onChange={(event) => { setReason(event.target.value); autoGrow(event.currentTarget); }}
            />
          </label>
          {error && <p className="error-message">{error}</p>}
          <div className="button-row">
            <button className="secondary-button compact-button" type="button" disabled={busy} onClick={() => void fire()}>
              {t("partnerBriefs.regenerate.confirmButton")}
            </button>
            <button className="quiet-link" type="button" disabled={busy} onClick={() => { setArmed(false); setError(null); }}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
